/**
 * RESPONSIBILITY
 *   Start the map on intent: when someone points at, focuses or touches a link to the map, load the map page's code, the map engine chunk
 *   (`MapEngineGate`) and
 *   make sure the map's boot has started (`ViewerBootService.boot()`, idempotent: the shell starts it at idle anyway), so
 *   the click lands on a map that is already on its way.
 *
 * RULES
 *   - Intent only: one capture-phase listener on the document for `pointerover`, `focusin` and `touchstart`. Nothing runs at
 *     app start, and the idle route preloader and Home's preload stay as they are.
 *   - Never on Data Saver or a 2G link: speculative downloads are paid for in someone's data (`prefersLightNetwork`).
 *   - Once per page load. Both tasks are what the map page does next anyway (the router's own chunk, the boot's own reads),
 *     so nothing is downloaded twice, and a failure is silent.
 *
 * USED BY
 *   `AppShellComponent`, which starts it once.
 */
import { Injectable, inject } from '@angular/core';
import { prefersLightNetwork } from '../utils/idle-work';
import { ViewerBootService } from './viewer-boot.service';
import { MapEngineGate } from '../map-engine/map-engine-gate.service';

/** A link to the employee map: `#/map`, `#/map?floor=…`, `/map`. Not `#/map-ingest`, `#/admin/map`. */
export function isMapHref(href: string | null | undefined): boolean {
  return !!href && /^(?:[^#]*#)?\/map(?:[/?]|$)/.test(href);
}

@Injectable({ providedIn: 'root' })
export class MapIntentPrefetchService {
  private readonly boot = inject(ViewerBootService);
  private readonly engine = inject(MapEngineGate);
  private started = false;
  private done = false;

  start(doc: Document = document): void {
    if (this.started || typeof doc === 'undefined') return;
    this.started = true;
    const onIntent = (event: Event): void => {
      if (this.done || prefersLightNetwork()) return;
      const target = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!isMapHref(target?.getAttribute('href'))) return;
      this.done = true;
      void import('../../features/viewer/viewer').catch(() => undefined);
      this.engine.prefetch();
      void this.boot.boot().catch(() => undefined);
    };
    for (const type of ['pointerover', 'focusin', 'touchstart']) {
      doc.addEventListener(type, onIntent, { capture: true, passive: true });
    }
  }
}
